<template>
  <div class="neighborhood-container" v-if="siblings.length">
    <div class="flexDiv mb-10">
      <h2 class="neighborhood-title">Select a Neighborhood</h2>
      <button
        v-if="showClearButton"
        @click="checkAndClearNeighborhood"
        class="btn-clr neighborhood-btn"
      >
        Clear
      </button>
    </div>
    <Carousel class="embheader_slider_wrap" v-bind="carouselConfig" v-model="currentSlide">
      <Slide class="relative" v-for="(sibling, idx) in siblings" :key="sibling.id">
          <NeighborhoodItem
            :sibling="sibling"
            :selectedNeighborhoodIds="selectedNeighborhoodIds"
            :neighborhoodLotsCount="neighborhoodLotsCount(sibling.id)"
            :neighborhoodSoldPercentage="neighborhoodSoldPercentage(sibling.id)"
            :soldPercentage="soldPercentage"
            :isComingSoon="isComingSoon(sibling)"
            :coordinates="coordinates"
            @loadNeighborhood="loadNeighborhood"
            @selectNeighborhood="payload => selectNeighborhood(payload, idx)"
            @hideSibling="hideSibling"
            @sethoverNeighborZoomLevel="sethoverNeighborZoomLevel"
            @highlightNeighborhood="highlightNeighborhood"
          />
          <AppHeaderCircle
            v-if="!isComingSoon(sibling)"
            class="progress-circle-absolute"
            :neighborhoodId="sibling.id"
            :soldPercentage="neighborhoodSoldPercentage(sibling.id)"
          />
      </Slide>

      <template #addons>
        <div class="carousel-navigation" v-if="siblings.length > 1">
          <Navigation />
        </div>
      </template>
    </Carousel>
  </div>
</template>

<script lang="ts" setup>
import { ref, toRefs, watch, onMounted, computed } from 'vue';
import { Carousel, Navigation, Slide } from 'vue3-carousel';
import type {
  NeighboorhoodCoords,
  NeighborhoodPlanQmiCounts,
  NeighborhoodPlatMap,
  NeighborhoodSoldDetails,
  Sibling,
} from '../types';
import NeighborhoodItem from './AppHeaderNeighborhoodListItem.vue';
import AppHeaderCircle from './AppHeaderCircle.vue';

interface Props {
  coordinates?: NeighborhoodPlatMap[];
  siblings: Sibling[];
  soldPercentage: NeighborhoodSoldDetails[];
  selectedNeighborhoodIds: string[];
  neighborhoodsPlanQmiCount: NeighborhoodPlanQmiCounts[];
  qmiFilter: boolean;
  isAnyFilterApplied: boolean;
  totalMobileFilterApplied: number;
  neighborhoodLotsCount: (neighborhoodId: string) => number;
  isComingSoon: (sibling: Sibling) => boolean;
}

const props = defineProps<Props>();
const {
  coordinates,
  siblings,
  soldPercentage,
  selectedNeighborhoodIds,
  isAnyFilterApplied,
  totalMobileFilterApplied,
  neighborhoodsPlanQmiCount,
  isComingSoon,
} = toRefs(props);

const carouselConfig = {
  itemsToShow: 1.4,
  itemsToScroll: 1,
  wrapAround: false,
};

const slick = ref<any>(null);
const currentSlide = ref(0);
const emit = defineEmits([
  'sethoverNeighborZoomLevel',
  'loadNeighborhood',
  'hideSibling',
  'clearSelectedNeighborhoods',
  'selectNeighborhood',
  'highlightNeighborhood',
]);

const sethoverNeighborZoomLevel = (payload: {
  zoomLevel: number;
  isDisableHoverNeighbor: boolean;
  centerMap: NeighboorhoodCoords;
}) => {
  emit('sethoverNeighborZoomLevel', payload);
};

const loadNeighborhood = (payload: {
  areaName: string;
  communityName: string;
  neighborhoodName: string;
}) => {
  emit('loadNeighborhood', payload);
};

const hideSibling = () => emit('hideSibling');
const clearSelectedNeighborhoods = () => emit('clearSelectedNeighborhoods');
const selectNeighborhood = (neighborhoodId: string, idx) => {
  currentSlide.value = idx;
  emit('selectNeighborhood', neighborhoodId);
};
const highlightNeighborhood = (payload: {
  neighborhoodId: string;
  display: string;
}) => emit('highlightNeighborhood', payload);

const checkAndClearNeighborhood = () => {
  if (
    isAnyFilterApplied.value ||
    selectedNeighborhoodIds.value.length ||
    totalMobileFilterApplied.value
  ) {
    currentSlide.value = 0;
    clearSelectedNeighborhoods();
  }
};

const showClearButton = computed(() => {
  return (
    isAnyFilterApplied.value ||
    selectedNeighborhoodIds.value.length ||
    totalMobileFilterApplied.value
  );
});

const previewCurrentNeighborhood = () => {
  if (selectedNeighborhoodIds.value.length) {
    const currentNeighborhoodIndex = siblings.value.findIndex(
      (s) => s.id === selectedNeighborhoodIds.value[0]
    );
    currentSlide.value = currentNeighborhoodIndex;
  }
};

watch(selectedNeighborhoodIds, () => {
  previewCurrentNeighborhood();
}, { deep: true });

onMounted(() => {
  previewCurrentNeighborhood();
});

const neighborhoodSoldPercentage = (neighborhoodId: string) => {
  const value = soldPercentage.value.find(
    (p) => p.neighborhoodId === neighborhoodId
  )?.soldPercentage;
  return value ? value : 0;
};
// const neighborhoodPlanQmisCount = (neighborhodId: string) => {
//   return neighborhoodsPlanQmiCount.value.find(
//     (neighborhood) => neighborhood.neighborhoodId === neighborhodId
//   );
// };
</script>

<style lang="scss">
.btn-clr {
  color: $lime-green-new;
  @include font(12px, 700);
  line-height: 14px;
  cursor: pointer;
  background-color: transparent;
  outline: none;
  border: none;
  letter-spacing: inherit;
}

.neighborhood {
  // &-container {
  //   @include md {
  //   padding-top:24px;
  //   }
  // }
  &-title {
    @include font(16px, 600);
    color: $white;
    margin-inline: auto;

    @include md {
      margin-inline: initial;
      font-size: 14px;
    }
  }

  &-btn {
    @include font(14px, 700);
  }
}

.embheader_slider_wrap {
  --ProgressTopMargin: 25px;
  --ProgressRightMargin: 20px;
  padding-inline: 12px;

  .carousel__slide {
    padding-bottom: var(--ProgressTopMargin);
  }

  .carousel__prev,
  .carousel__next {
    top: calc(50% - calc(var(--ProgressTopMargin, 2) / 1.4));
  }
}

.progress-circle-absolute {
  margin-top: calc(var(--ProgressTopMargin, 1) * -1);
  margin-right: var(--ProgressRightMargin);

  .progress-circle-container {
    margin-left: auto;
  }
}
</style>
